import { createContext, ReactElement, useState } from "react"
import axios from "utils/axios"
import {
  PaginatedPreRegistrations,
  PreRegistration,
  PreRegistrationsContextType,
  PreRegistrationsFilterType
} from "../types/preRegistrations"

const initialState: PaginatedPreRegistrations = {
  results: [],
  pagination: {
    previousPageToken: null,
    nextPageToken: null
  }
}

const PreRegistrationsContext = createContext<PreRegistrationsContextType | null>(null)

export const PreRegistrationsProvider = ({ children }: { children: ReactElement }) => {
  const [paginatedPreRegistrations, setPaginatedPreRegistrations] =
    useState<PaginatedPreRegistrations>(initialState)
  const [loading, setLoading] = useState(false)

  const fetchPreRegistrations = async (
    count: number = 20,
    pageToken?: string | null,
    filter?: PreRegistrationsFilterType,
    minDate?: string | null
  ) => {
    setLoading(true)
    try {
      let url = `/pre-registrations/?count=${count}`

      url += `&show_past=${filter?.showPastEvents ?? false}`
      url += `&show_canceled=${filter?.showCanceledEvents ?? false}`
      if (pageToken) {
        url += `&page_token=${encodeURIComponent(pageToken)}`
      }
      if (minDate) {
        url += `&min_date=${encodeURIComponent(minDate)}`
      }

      const response = await axios(url, { method: "get" })

      // Transform response from get_filtered_meetings format
      const collection = response.data.collection || []
      const transformedResults: PreRegistration[] = collection.map((meeting: any) => {
        const meetingId = meeting.uri?.replace("https://api.calendly.com/scheduled_events/", "") || ""
        return {
          calendly_id: meetingId,
          start_time: meeting.start_time || "",
          calendly_status: meeting.status || "",
          db_status: meeting.details?.db_status || "-",
          description: meeting.details?.description || "",
          created_at: meeting.created_at || "",
          name: meeting.details?.name || "-",
          email: meeting.details?.email || "-",
          phone_number: meeting.details?.phone_number || "-",
          country: meeting.details?.country || "-",
          role: meeting.details?.role || "-",
          artists: meeting.details?.artists || "-",
          hearing_source: meeting.details?.hearing_source || "-"
        }
      })

      setPaginatedPreRegistrations({
        results: transformedResults,
        pagination: {
          previousPageToken: response.data.pagination?.previous_page_token || null,
          nextPageToken: response.data.pagination?.next_page_token || null
        }
      })
    } catch (error) {
      console.error("Failed to fetch pre-registrations:", error)
      setPaginatedPreRegistrations(initialState)
    } finally {
      setLoading(false)
    }
  }

  return (
    <PreRegistrationsContext.Provider
      value={{ paginatedPreRegistrations, fetchPreRegistrations, loading }}
    >
      {children}
    </PreRegistrationsContext.Provider>
  )
}

export default PreRegistrationsContext
